import React from "react";
import { Icon } from "../../UIElements/Icon";

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
  showFirstLast = true,
  showPrevNext = true,
  className = "",
  disabled = false,
}) => {
  // Don't render if there's only one page or no pages
  if (totalPages <= 1) return null;

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);

    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const handlePageChange = (page) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage)
      return;
    onPageChange(page);
  };

  return (
    <div
      data-testid="pagination"
      className={`flex items-center justify-center space-x-1 ${className}`}
    >
      {/* First Page Button - Hidden on mobile */}
      {showFirstLast && (
        <button
          onClick={() => handlePageChange(1)}
          disabled={disabled || isFirstPage}
          className={`hidden rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:flex sm:px-3 sm:text-sm ${
            disabled || isFirstPage
              ? "cursor-not-allowed text-gray-500"
              : "text-gray-300 hover:bg-gray-700 hover:text-white"
          }`}
          title="First page"
        >
          <Icon name="ChevronsLeft" className="h-3 w-3 sm:h-4 sm:w-4" />
        </button>
      )}

      {/* Previous Page Button */}
      {showPrevNext && (
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || isFirstPage}
          className={`rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:px-3 sm:text-sm ${
            disabled || isFirstPage
              ? "cursor-not-allowed text-gray-500"
              : "text-gray-300 hover:bg-gray-700 hover:text-white"
          }`}
          title="Previous page"
        >
          <Icon name="ChevronLeft" className="h-3 w-3 sm:h-4 sm:w-4" />
        </button>
      )}

      {/* Page Numbers */}
      {showPageNumbers && (
        <>
          {/* Show ellipsis if there are pages before the visible range - Hidden on mobile */}
          {visiblePages[0] > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                disabled={disabled}
                className={`hidden rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:flex sm:px-3 sm:text-sm ${
                  disabled
                    ? "cursor-not-allowed text-gray-500"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                }`}
              >
                1
              </button>
              {visiblePages[0] > 2 && (
                <span className="hidden px-1 py-2 text-xs text-gray-500 sm:block sm:px-2 sm:text-sm">
                  ...
                </span>
              )}
            </>
          )}

          {/* Visible page numbers */}
          {visiblePages.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              disabled={disabled}
              className={`rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:px-3 sm:text-sm ${
                page === currentPage
                  ? "bg-blue-600 text-white"
                  : disabled
                    ? "cursor-not-allowed text-gray-500"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
              }`}
            >
              {page}
            </button>
          ))}

          {/* Show ellipsis if there are pages after the visible range - Hidden on mobile */}
          {visiblePages[visiblePages.length - 1] < totalPages && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <span className="hidden px-1 py-2 text-xs text-gray-500 sm:block sm:px-2 sm:text-sm">
                  ...
                </span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={disabled}
                className={`hidden rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:flex sm:px-3 sm:text-sm ${
                  disabled
                    ? "cursor-not-allowed text-gray-500"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
        </>
      )}

      {/* Next Page Button */}
      {showPrevNext && (
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || isLastPage}
          className={`rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:px-3 sm:text-sm ${
            disabled || isLastPage
              ? "cursor-not-allowed text-gray-500"
              : "text-gray-300 hover:bg-gray-700 hover:text-white"
          }`}
          title="Next page"
        >
          <Icon name="ChevronRight" className="h-3 w-3 sm:h-4 sm:w-4" />
        </button>
      )}

      {/* Last Page Button - Hidden on mobile */}
      {showFirstLast && (
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={disabled || isLastPage}
          className={`hidden rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:flex sm:px-3 sm:text-sm ${
            disabled || isLastPage
              ? "cursor-not-allowed text-gray-500"
              : "text-gray-300 hover:bg-gray-700 hover:text-white"
          }`}
          title="Last page"
        >
          <Icon name="ChevronsRight" className="h-3 w-3 sm:h-4 sm:w-4" />
        </button>
      )}
    </div>
  );
};

export default Pagination;
